import { useEffect, useState } from "react";
import { Building2, BookOpen, Globe, Clock, IndianRupee, DollarSign, Star, TrendingUp } from "lucide-react";
import api from "@/lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);
  if (!stats) return <div className="h-64 animate-pulse rounded-xl bg-slate-200" />;

  const cards = [
    { label: "Total Institutions", value: stats.total_institutions, icon: Building2 },
    { label: "Pending Institutions", value: stats.pending_institutions, icon: Clock, alert: stats.pending_institutions > 0 },
    { label: "Total Courses", value: stats.total_courses, icon: BookOpen },
    { label: "Pending Courses", value: stats.pending_courses, icon: Clock, alert: stats.pending_courses > 0 },
    { label: "Published Courses", value: stats.published_courses, icon: BookOpen },
    { label: "Countries", value: stats.countries, icon: Globe },
    { label: "Active Subscriptions", value: stats.active_subscriptions, icon: Star },
    { label: "Pending Cities", value: stats.pending_cities, icon: Clock, alert: stats.pending_cities > 0 },
  ];

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Dashboard</h1>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-xl border bg-white p-4 ${c.alert ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
            <c.icon className={`h-5 w-5 ${c.alert ? "text-amber-600" : "text-blue-800"}`} />
            <p className="mt-2 font-heading text-2xl font-bold text-slate-900">{c.value ?? 0}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2"><IndianRupee className="h-5 w-5 text-green-600" /><h3 className="font-heading font-semibold text-slate-900">Revenue (INR)</h3></div>
          <p className="mt-2 font-heading text-2xl font-bold text-slate-900">₹{(stats.revenue_inr || 0).toLocaleString("en-IN")}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-green-600" /><h3 className="font-heading font-semibold text-slate-900">Revenue (USD)</h3></div>
          <p className="mt-2 font-heading text-2xl font-bold text-slate-900">US$ {(stats.revenue_usd || 0).toLocaleString("en-US")}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-blue-800" /><h3 className="font-heading font-semibold text-slate-900">Plans</h3></div>
          <p className="mt-2 text-sm text-slate-600">Free: {stats.free_institutions} · Standard: {stats.standard_institutions} · Premium: {stats.premium_institutions}</p>
          <p className="text-sm text-slate-500">Total payments: {stats.total_payments}</p>
        </div>
      </div>

      {analytics && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Panel title="Most Searched" rows={analytics.top_searches.map((s) => [s.term, s.count])} cols={["Term", "Count"]} />
          <Panel title="Most Viewed Courses" rows={analytics.top_courses.map((c) => [c.name, c.views])} cols={["Course", "Views"]} />
          <Panel title="Top Universities" rows={analytics.top_universities.map((u) => [u.name, `${u.views} / ${u.clicks}`])} cols={["University", "Views / Clicks"]} />
        </div>
      )}
    </div>
  );
}

function Panel({ title, rows, cols }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="mb-3 font-heading font-semibold text-slate-900">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
        <table className="w-full text-left text-sm">
          <thead className="text-slate-400"><tr>{cols.map((c) => <th key={c} className="pb-2">{c}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100"><td className="py-1.5 pr-2 text-slate-700">{r[0]}</td><td className="py-1.5 text-slate-500">{r[1]}</td></tr>)}</tbody>
        </table>
      )}
    </div>
  );
}
